import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import createJob from '../functions/createJob';
import updateJob from '../functions/updateJob';
import createJobAttachment from '../functions/createJobAttachment';
import updateJobAttachment from '../functions/updateJobAttachment';
import getJobValuesToSave from '../functions/getJobValuesToSave';
import { IS_OPEN_FALSE } from '../assets/constants/jobConstants';
import api from '../../../apis/api';

export const SAVING = 'SAVING';
export const COMPLETE = 'COMPLETE';

const useJobActions = ({ handleSubmit }) => {
  const [actionStatus, setActionStatus] = useState();

  const onUpdateApplicant = useCallback(async (data) => {
    setActionStatus(SAVING);
    const {
      id,
      lastUpdate,
      attachment,
      applicants,
      name,
      company,
      comment,
      existingAttachment,
    } = data;
    const file = attachment[0];
    let updatedAttachmentName = existingAttachment.attachmentName;

    try {
      if (file.size !== 0) {
        // empty file was changed, update attachment

        // get presigned url for upload
        const jobAttachmentResponse = await updateJobAttachment(
          id,
          {
            attachmentName: file.name,
            type: file.type,
            oldAttachmentName: existingAttachment.attachmentName,
          },
          existingAttachment.attachmentId
        );
        const { url } = jobAttachmentResponse;
        updatedAttachmentName = file.name;

        // use presigned url to upload attachment
        await api.put(url, file, {
          headers: { 'Content-Type': file.type },
        });
      }

      // update job with updated applicant
      const updatedApplicant = {
        attachment: {
          attachmentName: updatedAttachmentName,
          attachmentId: existingAttachment.attachmentId,
        },
        name,
        company,
        comment,
      };

      const existingApplicants = applicants.filter(
        (applicant) =>
          applicant.attachment.attachmentId !== existingAttachment.attachmentId
      );

      const job = {
        id,
        lastUpdate,
        applicants: [...existingApplicants, updatedApplicant],
      };

      await updateJob(job);
      setActionStatus(COMPLETE);
      toast.success('Updated job applicant successfully.');
    } catch (error) {
      if (
        error.response &&
        error.response.data === 'The conditional request failed'
      ) {
        return toast.error(
          'Failed to update job applicant, job posting has been modified. Please refresh and try again.'
        );
      }
      toast.error('Failed to update job applicant.');
    }
    return true;
  }, []);

  const onSaveApplicant = useCallback(async (data) => {
    setActionStatus(SAVING);
    const { id, lastUpdate, attachment, applicants, name, company, comment } =
      data;
    const file = attachment[0];

    try {
      // get presigned url for upload
      const jobAttachmentResponse = await createJobAttachment(id, {
        attachmentName: file.name,
        type: file.type,
      });
      const { url, key } = jobAttachmentResponse;
      // use presigned url to upload attachment
      await api.put(url, file, {
        headers: { 'Content-Type': file.type },
      });

      // update job with new applicant
      const newApplicant = {
        attachment: {
          attachmentName: file.name,
          attachmentId: key,
        },
        name,
        company,
        comment,
      };
      const job = {
        id,
        lastUpdate,
        applicants: [...applicants, newApplicant],
      };

      await updateJob(job);
      toast.success('Added job applicant successfully.');
      setActionStatus(COMPLETE);
    } catch (error) {
      if (
        error.response &&
        error.response.data === 'The conditional request failed'
      ) {
        return toast.error(
          'Failed to add job applicant, job posting has been modified. Please refresh and try again.'
        );
      }
      toast.error('Failed to add job applicant.');
    }
    return true;
  }, []);

  const onClose = useCallback(async (data) => {
    setActionStatus(SAVING);
    const job = getJobValuesToSave(data);
    job.isOpen = IS_OPEN_FALSE;
    job.closedFilledDate = job.closedFilledDate.getTime();

    try {
      await updateJob(job);
      toast.success('Closed job posting successfully.');
    } catch (error) {
      if (
        error.response &&
        error.response.data === 'The conditional request failed'
      ) {
        return toast.error(
          'Failed to close, job posting has been modified. Please refresh and try again.'
        );
      }
      toast.error('Failed to close job posting.');
    } finally {
      setActionStatus(COMPLETE);
    }
    return true;
  }, []);

  const onUpdate = useCallback(async (data) => {
    setActionStatus(SAVING);
    const job = getJobValuesToSave(data);
    try {
      await updateJob(job);
      toast.success('Saved job posting successfully.');
    } catch (error) {
      if (
        error.response &&
        error.response.data === 'The conditional request failed'
      ) {
        return toast.error(
          'Failed to update, job posting has been modified. Please refresh and try again.'
        );
      }
      toast.error('Failed to update job posting.');
    } finally {
      setActionStatus(COMPLETE);
    }
    return true;
  }, []);

  const onSave = useCallback(async (data) => {
    setActionStatus(SAVING);
    const job = getJobValuesToSave(data);
    try {
      await createJob(job);
      toast.success('Saved job posting successfully.');
    } catch {
      toast.error('Failed to add job posting.');
    } finally {
      setActionStatus(COMPLETE);
    }
  }, []);

  const onSaveError = useCallback(() => {
    toast.error('Please fix form error(s) before saving.');
  }, []);

  const localSave = useCallback(
    (e) => {
      e.preventDefault();
      handleSubmit(onSave, onSaveError)(e);
    },
    [onSave, onSaveError, handleSubmit]
  );

  const localUpdate = useCallback(
    (e) => {
      e.preventDefault();
      handleSubmit(onUpdate, onSaveError)(e);
    },
    [onUpdate, onSaveError, handleSubmit]
  );

  const localClose = useCallback(
    (e) => {
      e.preventDefault();
      handleSubmit(onClose, onSaveError)(e);
    },
    [onClose, onSaveError, handleSubmit]
  );

  const localSaveApplicant = useCallback(
    (e) => {
      e.preventDefault();
      handleSubmit(onSaveApplicant, onSaveError)(e);
    },
    [onSaveApplicant, onSaveError, handleSubmit]
  );

  const localUpdateApplicant = useCallback(
    (e) => {
      e.preventDefault();
      handleSubmit(onUpdateApplicant, onSaveError)(e);
    },
    [onUpdateApplicant, onSaveError, handleSubmit]
  );

  return {
    close: localClose,
    update: localUpdate,
    save: localSave,
    saveApplicant: localSaveApplicant,
    updateApplicant: localUpdateApplicant,
    status: actionStatus,
  };
};

export default useJobActions;
